import type { DocumentMetadataImportFieldsFragment } from 'src/gql/generated/apollo-types';
import { DocumentMetadataImportStatus } from 'src/gql/generated/apollo-types';

import { FIRST_STEP, getWizardResumeState } from './wizard-import.helpers';
import type { WizardResumeState } from './wizard-import.helpers';

export const NAME_REQUIRED_ERROR = 'Data migration name is required.';

export interface WizardState {
  activeStep: number;
  displayName: string;
  draftImportId: string | null;
  generatedTemplateQuestionIds: string[] | null;
  operationError: string | null;
  /**
   * `null` means the user has not chosen an overwrite selection yet, so the
   * effective value falls back to the preview-derived default. Storing the
   * override (rather than the resolved set) avoids syncing server data into
   * editable state.
   */
  overwriteOverride: Set<string> | null;
  selectedQuestionIds: string[];
  uploadedFile: File | null;
  uploadResult: DocumentMetadataImportFieldsFragment | null;
}

export type WizardAction =
  | { type: 'reset' }
  | { resumeState: WizardResumeState; type: 'resumed' }
  | { displayName: string; type: 'displayNameChanged' }
  | { questionIds: string[]; type: 'questionsSelected' }
  | { draftImportId: string; questionIds: string[]; type: 'templateGenerated' }
  | { file: File; type: 'fileSelected' }
  | { type: 'fileCleared' }
  | { type: 'uploadValidated'; uploadResult: DocumentMetadataImportFieldsFragment }
  | { type: 'uploadMarkedFailed' }
  | { overwriteOverride: Set<string> | null; type: 'overwriteOverrideSet' }
  | { error: string | null; type: 'operationErrorSet' }
  | { stepCount: number; type: 'stepAdvanced' }
  | { type: 'stepReversed' };

export function initWizardState(
  existingImport?: DocumentMetadataImportFieldsFragment | null
): WizardState {
  return fromResumeState(getWizardResumeState(existingImport));
}

export function wizardReducer(state: WizardState, action: WizardAction): WizardState {
  switch (action.type) {
    case 'reset':
      return fromResumeState(getWizardResumeState(null));
    case 'resumed':
      return fromResumeState(action.resumeState);
    case 'displayNameChanged':
      return {
        ...state,
        displayName: action.displayName,
        operationError: state.operationError === NAME_REQUIRED_ERROR ? null : state.operationError,
      };
    case 'questionsSelected':
      return {
        ...state,
        overwriteOverride: null,
        selectedQuestionIds: action.questionIds,
        uploadResult: null,
        uploadedFile: null,
      };
    case 'templateGenerated':
      return {
        ...state,
        draftImportId: action.draftImportId,
        generatedTemplateQuestionIds: action.questionIds,
      };
    case 'fileSelected':
      return { ...state, operationError: null, uploadResult: null, uploadedFile: action.file };
    case 'fileCleared':
      return { ...state, uploadedFile: null };
    case 'uploadValidated':
      return { ...state, uploadResult: action.uploadResult };
    case 'uploadMarkedFailed':
      if (!state.uploadResult) return state;
      return {
        ...state,
        uploadResult: { ...state.uploadResult, status: DocumentMetadataImportStatus.Failed },
      };
    case 'overwriteOverrideSet':
      return { ...state, overwriteOverride: action.overwriteOverride };
    case 'operationErrorSet':
      return { ...state, operationError: action.error };
    case 'stepAdvanced':
      return { ...state, activeStep: Math.min(state.activeStep + 1, action.stepCount - 1) };
    case 'stepReversed':
      return { ...state, activeStep: Math.max(state.activeStep - 1, FIRST_STEP) };
  }
}

function fromResumeState(resumeState: WizardResumeState): WizardState {
  return {
    activeStep: resumeState.activeStep,
    displayName: resumeState.displayName,
    draftImportId: resumeState.draftImportId,
    generatedTemplateQuestionIds: resumeState.generatedTemplateQuestionIds,
    operationError: null,
    overwriteOverride: null,
    selectedQuestionIds: resumeState.selectedQuestionIds,
    uploadResult: resumeState.uploadResult,
    uploadedFile: resumeState.uploadedFile,
  };
}
